// Nombre completo de los controladores, para poder mostrar
// "Xavier Porta Controller - Controller5@aldahra.com" en vez del correo suelto.
//
// El nombre NO está en la base de logística: vive en la de usuarios
// (Portal_Users.AspNetUsers.FullName), a la que este backend no llega. Lo sirve
// el mismo endpoint que ya usa el portal Angular para su desplegable de
// controladores (planning-services.service.ts → GetAllControllerUsers), así que
// aquí se hace igual: una lectura, un diccionario correo→nombre, y el mapeo en
// cliente. Sin tocar el backend de cargas, que comparte la app Android.
//
// Notas de campo:
//  - Son ~18 usuarios (11 KB, ~600 ms). Se pide UNA vez por sesión y se cachea
//    un día en localStorage, no en IndexedDB: es un dato de presentación,
//    diminuto y prescindible, y no merece subir la versión de la base local ni
//    competir con la cola offline por las transacciones.
//  - Todo es best-effort: sin red, con caché caducada o con la respuesta rota,
//    la pantalla enseña el correo tal cual venía. Nunca bloquea ni rompe nada.
//  - Es un host distinto del gateway, sin token: se usa `fetch` directo en vez
//    del axios de la app para no contaminar la medición de latencia ni el
//    detector de lie-fi con una llamada que no es de la API de cargas.

import { useSyncExternalStore } from 'react';

const IDENTITY_USERS = 'https://aldahraidentityuser.azurewebsites.net';
// DOS listas, porque ninguna basta por sí sola:
//  - GetAllControllerUsers (18 usuarios, 11 KB): los del rol "Controller".
//  - GetAllFiltersAndAdminsUsers (34, 20 KB): admins y filtros, que también
//    aparecen como controladores de una carga — ahmed.dahab, por ejemplo, tiene
//    nombre en la base pero NO rol Controller, así que la primera lo deja fuera.
// Medido contra producción: la primera cubre 12 de los 13 controladores con
// cargas en los últimos 3 meses; con la segunda son 13 de 13. La primera manda:
// la segunda solo rellena huecos.
// (Existe /users/fullname?email=, que sería lo ideal para casos sueltos, pero
// responde 401 — exige una sesión de ese servidor que la PWA no tiene.)
const ENDPOINTS = [
  `${IDENTITY_USERS}/GetAllControllerUsers`,
  `${IDENTITY_USERS}/GetAllFiltersAndAdminsUsers`,
  // Los del rol Supplier (2026-09-10): su nombre sale junto al check de
  // "el proveedor ya ha rellenado algo" de la card. Lista mínima (sin hashes).
  `${IDENTITY_USERS}/GetAllSupplierUsers`,
];
const CACHE_KEY = 'CONTROLLER_NAMES_V1';
const TTL_MS = 24 * 60 * 60 * 1000;
// Corto a propósito: es un adorno del texto. Si tarda más, la carga se pinta
// con el correo y ya se resolverá en la siguiente apertura.
const TIMEOUT_MS = 8_000;

type NameMap = Record<string, string>;

interface CachedNames {
  at: number;
  names: NameMap;
}

// Diccionario en memoria para el render: se rellena desde la caché al primer
// uso y se actualiza cuando llega la respuesta de red.
let names: NameMap = {};
let loaded = false;
let inFlight: Promise<void> | null = null;

// Contador que cambia cada vez que el diccionario se actualiza: es el snapshot
// que observan los componentes para repintarse cuando llegan los nombres.
let version = 0;
const subscribers = new Set<() => void>();
function emit(): void {
  version++;
  subscribers.forEach((cb) => {
    try { cb(); } catch { /* subscriber roto */ }
  });
}

function normalize(email?: string | null): string {
  return (email ?? '').trim().toLowerCase();
}

interface RemoteUser {
  userName?: string;
  email?: string;
  fullName?: string;
}

// Una lista de usuarios. Devuelve [] ante cualquier problema: sin red, timeout,
// error HTTP o respuesta que no sea un array. Nunca lanza.
async function fetchUsers(url: string): Promise<RemoteUser[]> {
  try {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), TIMEOUT_MS);
    const r = await fetch(url, { signal: controller.signal }).finally(() => clearTimeout(timer));
    if (!r.ok) return [];
    const data = await r.json();
    return Array.isArray(data) ? (data as RemoteUser[]) : [];
  } catch {
    return [];
  }
}

function readCache(): CachedNames | null {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CachedNames;
    if (!parsed || typeof parsed.at !== 'number' || !parsed.names) return null;
    return parsed;
  } catch {
    return null;
  }
}

/**
 * Nombre completo de un controlador, o undefined si no se conoce.
 * Pensado para llamarse en el render: solo lee el diccionario en memoria.
 */
export function controllerFullName(email?: string | null): string | undefined {
  const key = normalize(email);
  if (!key) return undefined;
  return names[key] || undefined;
}

/**
 * Etiqueta a mostrar, con el mismo formato que el portal:
 *   "Xavier Porta Controller - Controller5@aldahra.com"
 * Sin nombre conocido devuelve el correo tal cual, que es lo que se veía antes.
 */
export function controllerLabel(email?: string | null): string {
  const raw = (email ?? '').trim();
  if (!raw) return '';
  const full = controllerFullName(raw);
  return full ? `${full} - ${raw}` : raw;
}

/**
 * Carga los nombres: primero de la caché (inmediato) y, si está vencida, los
 * refresca por red. Idempotente y sin concurrencia — llamarla de más es gratis.
 */
export function ensureControllerNames(): Promise<void> {
  if (!loaded) {
    loaded = true;
    const cached = readCache();
    if (cached) {
      names = cached.names;
      emit();
      // Caché fresca: nada más que hacer hasta mañana.
      if (Date.now() - cached.at < TTL_MS) return Promise.resolve();
    }
  } else if (inFlight == null) {
    const cached = readCache();
    if (cached && Date.now() - cached.at < TTL_MS) return Promise.resolve();
  }
  if (inFlight) return inFlight;

  inFlight = (async () => {
    try {
      // En paralelo: si una falla, la otra sigue valiendo.
      const listas = await Promise.all(ENDPOINTS.map(fetchUsers));
      const next: NameMap = {};
      for (const lista of listas) {
        for (const u of lista) {
          const key = normalize(u?.userName ?? u?.email);
          const full = (u?.fullName ?? '').trim();
          // El primero que llega manda: la lista de controladores tiene
          // prioridad sobre la de admins/filtros.
          if (key && full && !next[key]) next[key] = full;
        }
      }
      // Una respuesta vacía no debe borrar lo que ya teníamos: preferimos un
      // diccionario viejo a quedarnos sin nombres.
      if (Object.keys(next).length === 0) return;
      names = next;
      try {
        localStorage.setItem(CACHE_KEY, JSON.stringify({ at: Date.now(), names: next } as CachedNames));
      } catch { /* sin espacio: seguimos con el diccionario en memoria */ }
      emit();
    } catch {
      /* sin red, timeout o respuesta rota: se muestra el correo y ya está */
    } finally {
      inFlight = null;
    }
  })();
  return inFlight;
}

/** Suscripción para que la lista se repinte cuando lleguen los nombres. */
export function subscribeControllerNames(cb: () => void): () => void {
  subscribers.add(cb);
  return () => subscribers.delete(cb);
}

/**
 * Suscribe el componente a las actualizaciones del diccionario. Devuelve un
 * contador — no se usa su valor, solo hace que el render se repita cuando los
 * nombres llegan por red después de haber pintado los correos.
 */
export function useControllerNames(): number {
  return useSyncExternalStore(subscribeControllerNames, () => version, () => version);
}
